<template>
  <div class="w-full p-2">
    <Tabs v-model:activeKey="activeView" class="mode-share-view-tabs">
      <TabPane key="charts" tab="Charts">
        <div v-if="chartTypeComp == 'Initial'">
          <Button
            type="primary"
            size="large"
            class="btn-gradient"
            @click="
              () => {
                connectedCharts = ~connectedCharts;
                updateConnectedStore(connectedCharts);
                handleChange([]);
              }
            "
          >
            <span v-if="connectedCharts">Disconnect Charts</span>
            <span v-else>Connect Charts</span>
          </Button>
        </div>
        <div v-if="chartTypeComp != 'Initial'">
          <Button
            type="primary"
            size="large"
            class="btn-gradient"
            @click="
              () => {
                connectedCharts2 = ~connectedCharts2;
                handleChange([]);
              }
            "
          >
            <span v-if="connectedCharts2">Disconnect Charts</span>
            <span v-else>Connect Charts</span>
          </Button>
        </div>
        <div class="w-full flex justify-center">
          <div v-if="connectedCharts">
            <Select
              mode="multiple"
              style="width: 100%"
              class="min-w-[130px]"
              placeholder="Please select"
              :options="searchDayData"
              @change="handleChange"
            >
            </Select>
          </div>
        </div>
        <div class="chart-container">
          <div class="mt-6 grid grid-cols-1 xl:grid-cols-2 gap-10 items-end">
            <div v-if="chartTypeComp == 'Initial'">
              <MainChart
                :query="initialQuery"
                :data="initialData"
                @setDate="setInitialDate"
                @updateDefaultType="updateDefaultType"
                :connectedCharts="connectedCharts || connectedCharts2"
                defaultType="Initial"
                :show-yearly="true"
              />
            </div>
            <div v-else>
              <MainChart
                :query="filiterQueryCompare"
                :data="filiterCompareData"
                :defaultYear="primaryPeriodValue"
                :defaultType="primaryChartType"
                :default-index="primaryChartIndex"
                :default-range="initialDateCompare"
                @setDate="setFiliterDateCompare"
                :showInitial="true"
                :show-yearly="true"
                :connectedCharts="connectedCharts || connectedCharts2"
                @updateDefaultType="updateDefaultType"
              />
            </div>
            <MainChart
              :query="filiterQuery"
              :data="filiterData"
              :defaultYear="filiterPeriodValue"
              :defaultType="filiterChartType"
              :default-index="filiterChartIndex"
              :default-range="filiterDate"
              :search-day-show="true"
              :show-yearly="true"
              :connectedCharts="connectedCharts || connectedCharts2"
              @setDate="setFiliterDate"
              @updateDefaultType="setFiliterPeriodTypeFromChart"
              :startMonth="startMonth"
            />
          </div>
        </div>
        <ReportChart
          queryname="modeShareBehaviorStatsQuery"
          gettername="modeShareBehaviorStatsData"
          :commutersData="commutersData"
          :year="reportYear"
          title="Commute Behavior Report"
          filter="Trips"
          @setDate="setReportDate"
        />
      </TabPane>

      <TabPane key="table" tab="Table">
        <div class="mt-6 space-y-8">
          <section>
            <div class="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 class="text-lg font-semibold text-primary">Mode Share Comparison</h3>
                <span class="text-sm text-gray-500">
                  {{ primaryTableTitle }} vs Selected Mode Share
                </span>
              </div>
              <Select
                class="min-w-[150px]"
                :value="modeSummaryView"
                :options="modeSummaryViewOptions"
                @change="setModeSummaryView"
              />
            </div>
            <div class="mb-4 grid grid-cols-1 xl:grid-cols-2 gap-8">
              <div class="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h4 class="font-medium text-primary">{{ primaryTableTitle }}</h4>
                  <span class="text-sm text-gray-500">{{ totalCommutersText(primaryTableData) }}</span>
                </div>
                <div class="flex flex-wrap items-center gap-2">
                  <Select
                    class="min-w-[150px]"
                    :value="primaryPeriodType"
                    :options="primaryPeriodOptions"
                    @change="setPrimaryPeriodType"
                  />
                  <date-picker
                    v-if="primaryPeriodType !== 'Initial' && primaryPeriodType !== 'Range'"
                    :value="primaryPeriodValue"
                    :picker="pickerForPeriod(primaryPeriodType)"
                    :allowClear="false"
                    @change="setPrimaryPeriodValue"
                  />
                  <RangePicker
                    v-if="primaryPeriodType === 'Range'"
                    :value="primaryRangeValue"
                    :allowClear="false"
                    @change="setPrimaryRange"
                  />
                </div>
              </div>
              <div class="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h4 class="font-medium text-primary">Selected Mode Share</h4>
                  <span class="text-sm text-gray-500">{{ totalCommutersText(filiterData) }}</span>
                </div>
                <div class="flex flex-wrap items-center gap-2">
                  <Select
                    class="min-w-[150px]"
                    :value="filiterPeriodType"
                    :options="periodOptions"
                    @change="setFiliterPeriodType"
                  />
                  <date-picker
                    v-if="filiterPeriodType !== 'Range'"
                    :value="filiterPeriodValue"
                    :picker="pickerForPeriod(filiterPeriodType)"
                    :allowClear="false"
                    @change="setFiliterPeriodValue"
                  />
                  <RangePicker
                    v-if="filiterPeriodType === 'Range'"
                    :value="filiterRangeValue"
                    :allowClear="false"
                    @change="setFiliterRange"
                  />
                </div>
              </div>
            </div>
            <Table
              row-key="key"
              size="middle"
              :pagination="false"
              :columns="combinedModeSummaryColumns"
              :data-source="combinedModeTableRows"
              :loading="isCombinedModeTableLoading"
              :scroll="{ x: 1200 }"
            />
          </section>

          <section>
            <div class="mb-3 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 class="text-lg font-semibold text-primary">Commute Behavior Report</h3>
                <span class="text-sm text-gray-500">{{ reportYearLabel }}</span>
              </div>
              <div class="flex flex-wrap items-center gap-2">
                <Select
                  class="min-w-[150px]"
                  :value="behaviorSummaryView"
                  :options="behaviorSummaryViewOptions"
                  @change="setBehaviorSummaryView"
                />
                <date-picker
                  :value="reportYear"
                  picker="year"
                  :allowClear="false"
                  @change="setReportDate"
                />
              </div>
            </div>
            <Table
              row-key="key"
              size="middle"
              :pagination="false"
              :columns="behaviorTableColumns"
              :data-source="behaviorTableRows"
              :loading="isQueryLoading(reportQuery)"
              :scroll="{ x: 1500 }"
            />
          </section>
        </div>
      </TabPane>
    </Tabs>

    <!-- api queries  -->
    <StatsModeShareInitialQuery
      :from="initialDate[0]"
      :to="initialDate[1]"
      :daySearch="searchDay"
      :key="initialDate[0] + initialDate[1] + searchDay"
    />
    <StatsModeMonthQuery
      v-if="filiterDate"
      :from="filiterDate[0]"
      :to="filiterDate[1]"
      :daySearch="searchDay"
      :key="filiterDate[0] + filiterDate[1] + searchDay"
    />
    <StatsModeMonthQueryCompare
      v-if="initialDateCompare"
      :from="initialDateCompare[0]"
      :to="initialDateCompare[1]"
      :from_connect="filiterDate[0]"
      :to_connect="filiterDate[1]"
      :connect2="connectedCharts2"
      :daySearch="searchDay"
      :key="
        initialDateCompare[0] +
        initialDateCompare[1] +
        searchDay +
        connectedCharts2 +
        filiterDate[0] +
        filiterDate[1]
      "
      :compare="true"
    />

    <StatsModeMonthQuery
      v-if="filiterDate"
      :from="filiterDate[0]"
      :to="filiterDate[1]"
      :daySearch="searchDay"
      :from_connect="initialDateCompare[0]"
      :to_connect="initialDateCompare[1]"
      :connect2="connectedCharts2"
      :key="
        filiterDate[0] +
        filiterDate[1] +
        searchDay +
        connectedCharts2 +
        initialDateCompare[0] +
        initialDateCompare[1]
      "
    />

    <StatsModeShareBehaviorQuery
      v-if="reportDate"
      :from="reportDate[0]"
      :to="reportDate[1]"
      :daySearch="searchDay"
      :key="reportDate[0] + reportDate[1] + searchDay"
    />

    <StatsModeShareCommutersQuery
      v-if="reportDate"
      :from="reportDate[0]"
      :to="reportDate[1]"
      :daySearch="searchDay"
      :key="reportDate[0] + reportDate[1] + searchDay"
    />
  </div>
</template>

<script>
/* eslint-disable */
// eslint-disable-next-line import/no-duplicates
import StatsModeShareInitialQuery from '@/services/queries/stats/StatsModeShareInitialQuery.vue';
// eslint-disable-next-line import/no-duplicates
import StatsModeMonthQueryCompare from '@/services/queries/stats/StatsModeMonthQuery.vue';
import StatsModeShareBehaviorQuery from '@/services/queries/stats/StatsModeShareBehaviorQuery.vue';
// eslint-disable-next-line import/no-duplicates
import StatsModeMonthQuery from '@/services/queries/stats/StatsModeMonthQuery.vue';
import StatsModeShareCommutersQuery from '@/services/queries/stats/StatsModeShareCommutersQuery.vue';
import { STATS } from '@/store/types';

import {
  Button,
  DatePicker,
  RangePicker,
  Skeleton,
  notification,
  Select,
  Table,
  Tabs,
  TabPane,
} from 'ant-design-vue';
import dayjs from 'dayjs';
import ReportChart from './charts/ReportChart.vue';

import MainChart from './charts/MainChart.vue';
import debounce from 'lodash.debounce';

import getActiveMonth from '../shared/getActiveMonth';
import { MONTHES, sortAndNameMonthesAndCommuters } from '@/utils/monthes';

export default {
  components: {
    ReportChart,
    Button,
    StatsModeShareInitialQuery,
    StatsModeShareBehaviorQuery,
    StatsModeMonthQuery,
    MainChart,
    StatsModeShareCommutersQuery,
    StatsModeMonthQueryCompare,
    Skeleton,
    notification,
    Select,
    DatePicker,
    RangePicker,
    Table,
    Tabs,
    TabPane,
  },

  data() {
    return {
      reportYear: dayjs(),
      defaultFilterYear: dayjs(),
      reportDate: null,
      filiterDate: null,
      initialDate: [null, null],
      initialDateConnect: [null, null],
      startMonth: 1,
      connectedCharts: false,
      connectedCharts2: false,
      activeView: 'charts',
      primaryPeriodType: 'Initial',
      primaryPeriodValue: dayjs(),
      filiterPeriodType: 'Monthly',
      filiterPeriodValue: dayjs(),
      checkLoading: false,
      filterDataChanged: false,
      chartType: 'Initial',
      modeSummaryView: 'Both',
      modeSummaryViewOptions: [
        { value: 'Trips', label: 'Trips' },
        { value: 'Share', label: 'Percentage' },
        { value: 'Both', label: 'Trips + Percentage' },
      ],
      periodOptions: [
        { value: 'Weekly', label: 'Weekly' },
        { value: 'Monthly', label: 'Monthly' },
        { value: 'Quarterly', label: 'Quarterly' },
        { value: 'Yearly', label: 'Yearly' },
      ],
      primaryPeriodOptions: [
        { value: 'Initial', label: 'Initial' },
        { value: 'Monthly', label: 'Monthly' },
        { value: 'Quarterly', label: 'Quarterly' },
        { value: 'Yearly', label: 'Yearly' },
      ],
      searchDayData: [
        { id: 1, value: 'Sunday' },
        { id: 2, value: 'Monday' },
        { id: 3, value: 'Tuesday' },
        { id: 4, value: 'Wednesday' },
        { id: 5, value: 'Thursday' },
        { id: 6, value: 'Friday' },
        { id: 7, value: 'Saturday' },
      ],

      initialDateCompare: [null, null],
      loading: false,
      componentKey: 0,
      behaviorSummaryView: 'Both',
      behaviorSummaryViewOptions: [
        { value: 'Trips', label: 'Trips' },
        { value: 'Share', label: 'Percentage' },
        { value: 'Both', label: 'Trips + Percentage' },
      ],
    };
  },

  created() {
    const currYear = dayjs().year();
    const yearDate = [`${currYear}-01-01`, `${currYear}-12-31`];

    if (!this.reportDate) {
      this.reportDate = yearDate;
    }
    if (!this.filiterDate) {
      this.filiterDate = yearDate;
    }
    this.primaryPeriodValue = dayjs(yearDate[0]);
    this.filiterPeriodValue = dayjs(yearDate[0]);
    this.debouncedHandleChange = debounce((value) => {
      this.searchDay = value;
      this.$store.commit(STATS.SET_EMPLOYER_DAY_SEARCH_QUERY, value);
    }, 500); // 300ms delay - adjust as needed
    this.initialDateCompare = yearDate;
  },
  errorCaptured() {
    this.openNotification();
    setTimeout(() => {
      window.location.reload();
    }, 2000);
  },
  computed: {
    initialQuery() {
      return this.$store.state.stats.modeShareInitialStatsQuery;
    },
    initialData() {
      return this.$store.getters.modeShareInitialStatsData;
    },
    filiterQuery() {
      return this.$store.state.stats.modeShareMonthStatsQuery;
    },
    filiterQueryCompare() {
      return this.$store.state.stats.modeShareMonthStatsCompareQuery;
    },
    searchDay() {
      return this.$store.state.stats.searchDay;
    },
    filiterData() {
      return this.$store.getters.modeShareMonthStatsData;
    },
    filiterCompareData() {
      return this.$store.getters.modeShareMonthStatsCompareData;
    },
    reportData() {
      return this.$store.getters.modeShareBehaviorStatsData;
    },
    commutersData() {
      return this.$store.getters.modeShareCommutersData;
    },
    reportQuery() {
      return this.$store.state.stats.modeShareBehaviorStatsQuery;
    },
    chartTypeComp() {
      return this.chartType;
    },
    isLoading() {
      return this.loading;
    },
    primaryRangeValue() {
      return this.toPickerRange(this.initialDateCompare);
    },
    filiterRangeValue() {
      return this.toPickerRange(this.filiterDate);
    },
    primaryChartType() {
      return this.primaryPeriodType;
    },
    filiterChartType() {
      return this.filiterPeriodType;
    },
    primaryChartIndex() {
      return this.getPeriodIndex(this.primaryChartType, this.primaryPeriodValue);
    },
    filiterChartIndex() {
      return this.getPeriodIndex(this.filiterChartType, this.filiterPeriodValue);
    },
    primaryTableTitle() {
      return this.primaryPeriodType == 'Initial'
        ? 'Initial Mode Share'
        : `${this.primaryPeriodType} Mode Share`;
    },
    primaryTableData() {
      return this.chartTypeComp == 'Initial' ? this.initialData : this.filiterCompareData;
    },
    primaryTableQuery() {
      return this.chartTypeComp == 'Initial' ? this.initialQuery : this.filiterQueryCompare;
    },
    primaryColumnPrefix() {
      return this.primaryPeriodType === 'Initial' ? 'Initial' : this.primaryPeriodType;
    },
    primaryPeriodLabel() {
      return this.primaryPeriodType === 'Initial'
        ? 'Initial Reported Modes'
        : this.formatPeriodLabel(this.primaryPeriodType, this.primaryPeriodValue, this.primaryRangeValue);
    },
    selectedPeriodLabel() {
      return this.formatPeriodLabel(this.filiterPeriodType, this.filiterPeriodValue, this.filiterRangeValue);
    },
    combinedModeTableRows() {
      return this.buildCombinedModeRows(this.primaryTableData, this.filiterData);
    },
    isCombinedModeTableLoading() {
      return (
        this.isQueryLoading(this.primaryTableQuery) || this.isQueryLoading(this.filiterQuery)
      );
    },
    behaviorTableRows() {
      return this.buildBehaviorRows(this.reportData);
    },
    combinedModeSummaryColumns() {
      const columns = [
        {
          title: 'Mode',
          dataIndex: 'label',
          key: 'label',
          sorter: (a, b) => a.label.localeCompare(b.label),
        },
      ];

      if (this.modeSummaryView === 'Both') {
        columns.push({
          title: this.primaryPeriodLabel,
          dataIndex: 'primaryCombinedFormatted',
          key: 'primaryCombined',
          sorter: (a, b) => (a.primaryTrips ?? 0) - (b.primaryTrips ?? 0),
        });
        columns.push({
          title: this.selectedPeriodLabel,
          dataIndex: 'selectedCombinedFormatted',
          key: 'selectedCombined',
          sorter: (a, b) => (a.selectedTrips ?? 0) - (b.selectedTrips ?? 0),
        });
        columns.push({
          title: 'Difference',
          dataIndex: 'diffCombinedFormatted',
          key: 'diffCombined',
          sorter: (a, b) => (a.tripsDiff ?? 0) - (b.tripsDiff ?? 0),
        });
      }

      if (this.modeSummaryView === 'Trips') {
        columns.push({
          title: this.primaryPeriodType === 'Initial' ? 'Initial Reported Modes' : `${this.primaryPeriodLabel} Trips`,
          dataIndex: 'primaryTripsFormatted',
          key: 'primaryTrips',
          sorter: (a, b) => (a.primaryTrips ?? 0) - (b.primaryTrips ?? 0),
        });
        columns.push({
          title: `${this.selectedPeriodLabel} Trips`,
          dataIndex: 'selectedTripsFormatted',
          key: 'selectedTrips',
          sorter: (a, b) => (a.selectedTrips ?? 0) - (b.selectedTrips ?? 0),
        });
        columns.push({
          title: 'Trips Diff',
          dataIndex: 'tripsDiffFormatted',
          key: 'tripsDiff',
          sorter: (a, b) => (a.tripsDiff ?? 0) - (b.tripsDiff ?? 0),
        });
      }

      if (this.modeSummaryView === 'Share') {
        columns.push({
          title: `${this.primaryPeriodLabel} Percentage`,
          dataIndex: 'primaryShareFormatted',
          key: 'primaryShare',
          sorter: (a, b) => (a.primaryShare ?? 0) - (b.primaryShare ?? 0),
        });
        columns.push({
          title: `${this.selectedPeriodLabel} Percentage`,
          dataIndex: 'selectedShareFormatted',
          key: 'selectedShare',
          sorter: (a, b) => (a.selectedShare ?? 0) - (b.selectedShare ?? 0),
        });
        columns.push({
          title: 'Percentage Diff',
          dataIndex: 'shareDiffFormatted',
          key: 'shareDiff',
          sorter: (a, b) => (a.shareDiff ?? 0) - (b.shareDiff ?? 0),
        });
      }

      return columns;
    },
    behaviorTableColumns() {
      const columns = [
        {
          title: 'Mode',
          dataIndex: 'label',
          key: 'label',
          sorter: (a, b) => a.label.localeCompare(b.label),
        },
      ];

      if (this.reportData && this.reportData.initial) {
        if (this.behaviorSummaryView === 'Both') {
          columns.push({
            title: 'Initial Reported Modes',
            dataIndex: 'initialCombinedFormatted',
            key: 'initialCombined',
            sorter: (a, b) => a.initialTrips - b.initialTrips,
          });
        } else {
          if (this.behaviorSummaryView === 'Trips') {
            columns.push({
              title: 'Initial Reported Modes',
              dataIndex: 'initialTripsFormatted',
              key: 'initialTrips',
              sorter: (a, b) => a.initialTrips - b.initialTrips,
            });
          }
          if (this.behaviorSummaryView === 'Share') {
            columns.push({
              title: 'Initial Percentage',
              dataIndex: 'initialShareFormatted',
              key: 'initialShare',
              sorter: (a, b) => a.initialShare - b.initialShare,
            });
          }
        }
      }

      MONTHES.forEach((month) => {
        const key = this.monthKey(month);

        if (this.behaviorSummaryView === 'Both') {
          columns.push({
            title: month,
            dataIndex: `${key}CombinedFormatted`,
            key: `${key}Combined`,
            sorter: (a, b) => a[`${key}Trips`] - b[`${key}Trips`],
          });
        } else {
          if (this.behaviorSummaryView === 'Trips') {
            columns.push({
              title: `${month} Trips`,
              dataIndex: `${key}TripsFormatted`,
              key: `${key}Trips`,
              sorter: (a, b) => a[`${key}Trips`] - b[`${key}Trips`],
            });
          }
          if (this.behaviorSummaryView === 'Share') {
            columns.push({
              title: `${month} Percentage`,
              dataIndex: `${key}ShareFormatted`,
              key: `${key}Share`,
              sorter: (a, b) => a[`${key}Share`] - b[`${key}Share`],
            });
          }
        }
      });

      if (this.behaviorSummaryView === 'Both') {
        columns.push({
          title: 'Total',
          dataIndex: 'totalCombinedFormatted',
          key: 'totalCombined',
          sorter: (a, b) => a.totalTrips - b.totalTrips,
        });
      } else {
        if (this.behaviorSummaryView === 'Trips') {
          columns.push({
            title: 'Total Trips',
            dataIndex: 'totalTripsFormatted',
            key: 'totalTrips',
            sorter: (a, b) => a.totalTrips - b.totalTrips,
          });
        }
        if (this.behaviorSummaryView === 'Share') {
          columns.push({
            title: 'Total Percentage',
            dataIndex: 'totalShareFormatted',
            key: 'totalShare',
            sorter: (a, b) => a.totalShare - b.totalShare,
          });
        }
      }

      return columns;
    },
    reportYearLabel() {
      return dayjs(this.reportYear).format('YYYY');
    },
  },
  watch: {
    reportData: {
      async handler(val) {
        if (!this.checkLoading && val && !this.filterDataChanged) {
          this.checkLoading = true;
          const { firstMonth, year } = await getActiveMonth(val, this.startMonth);

          if (firstMonth) {
            this.startMonth = firstMonth;
            this.filterDataChanged = true;
          }
          if (year && year !== dayjs(this.defaultFilterYear).year()) {
            const endMon = dayjs(`${year}-${firstMonth ? firstMonth + 1 : 1}-01`)
              .endOf('month')
              .format('YYYY-MM-DD');
            this.setFiliterDate([`${year}-01-01`, endMon], 'Range');
            this.defaultFilterYear = dayjs().year(year);

            // change behavior report chart default year
            this.reportYear = dayjs().year(year);
            const currYear = dayjs().year(year).year();
            this.reportDate = [`${currYear}-01-01`, `${currYear}-12-31`];
          }
          this.checkLoading = false;
        }
      },
      deep: true,
    },
    connectedCharts(val) {
      if (val) {
        this.initialDate = this.filiterDate;
        this.initialDateCompare = this.filiterDate;
      } else {
        this.initialDate = [null, null];
      }
    },
    // filiterData(val) {
    //   if (val && val.no_commuters === 0) {
    //   }
    // },
  },
  methods: {
    getOrdinalSuffix(day) {
      const value = Number(day);
      const mod100 = value % 100;

      if (mod100 >= 11 && mod100 <= 13) return 'th';

      switch (value % 10) {
        case 1:
          return 'st';
        case 2:
          return 'nd';
        case 3:
          return 'rd';
        default:
          return 'th';
      }
    },
    formatPeriodLabel(type, value, range = []) {
      const date = dayjs(range?.[0] || value || dayjs());

      if (type === 'Weekly') {
        return `Week of ${date.format('MMMM D')}${this.getOrdinalSuffix(date.date())}`;
      }
      if (type === 'Quarterly') {
        return `Q${Math.floor(date.month() / 3) + 1} ${date.format('YYYY')}`;
      }
      if (type === 'Yearly') {
        return date.format('YYYY');
      }
      if (type === 'Range') {
        const endDate = dayjs(range?.[1] || value || dayjs());
        return `${date.format('MMM D, YYYY')} - ${endDate.format('MMM D, YYYY')}`;
      }

      return date.format('MMMM YYYY');
    },
    formatCombinedValue(trips, share) {
      return `${this.formatWholeNumber(trips)} (${this.formatPercent(share)})`;
    },
    isQueryLoading(query) {
      return Boolean(query && (query.isFetching || query.isLoading));
    },
    pickerForPeriod(type) {
      if (type === 'Weekly') {
        return 'week';
      }
      if (type === 'Quarterly') {
        return 'quarter';
      }
      return type === 'Yearly' ? 'year' : 'month';
    },
    getPeriodDates(type, value) {
      const date = dayjs(value || dayjs());

      if (type === 'Weekly') {
        const start = date.day() === 0 ? date.subtract(6, 'day') : date.subtract(date.day() - 1, 'day');
        const end = start.add(6, 'day');

        return [start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD')];
      }

      if (type === 'Yearly') {
        return [
          date.startOf('year').format('YYYY-MM-DD'),
          date.endOf('year').format('YYYY-MM-DD'),
        ];
      }

      if (type === 'Quarterly') {
        const startMonth = Math.floor(date.month() / 3) * 3;
        const start = date.month(startMonth).startOf('month');
        const end = start.add(2, 'month').endOf('month');

        return [start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD')];
      }

      return [
        date.startOf('month').format('YYYY-MM-DD'),
        date.endOf('month').format('YYYY-MM-DD'),
      ];
    },
    toPickerRange(dates) {
      if (!dates || !dates[0] || !dates[1]) return [];

      return [dayjs(dates[0]), dayjs(dates[1])];
    },
    formatPickerRange(dates) {
      if (!dates || !dates[0] || !dates[1]) return null;

      return [dayjs(dates[0]).format('YYYY-MM-DD'), dayjs(dates[1]).format('YYYY-MM-DD')];
    },
    getWeekIndexFromDate(value) {
      const date = dayjs(value || dayjs()).toDate();
      const year = date.getFullYear();
      const janFirst = new Date(year, 0, 1);
      const dayOfWeek = janFirst.getDay();
      const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      const firstMonday = new Date(janFirst);
      firstMonday.setDate(janFirst.getDate() - daysToMonday);

      const selectedDayOfWeek = date.getDay();
      const daysToSelectedMonday = selectedDayOfWeek === 0 ? 6 : selectedDayOfWeek - 1;
      const selectedMonday = new Date(date);
      selectedMonday.setDate(date.getDate() - daysToSelectedMonday);

      // Use UTC day counts so DST shifts can't skew the week math
      const diffDays = Math.round(
        (Date.UTC(
          selectedMonday.getFullYear(),
          selectedMonday.getMonth(),
          selectedMonday.getDate(),
        ) -
          Date.UTC(firstMonday.getFullYear(), firstMonday.getMonth(), firstMonday.getDate())) /
          (24 * 60 * 60 * 1000),
      );

      return Math.max(0, Math.round(diffDays / 7));
    },
    getPeriodIndex(type, value) {
      const date = dayjs(value || dayjs());

      if (type === 'Weekly') {
        return this.getWeekIndexFromDate(date);
      }
      if (type === 'Quarterly') {
        return Math.floor(date.month() / 3);
      }
      if (type === 'Monthly') {
        return date.month();
      }

      return 0;
    },
    setPrimaryInputFromDates(dates, type = this.primaryPeriodType) {
      if (!dates || !dates[0]) return;

      if (type === 'Initial') {
        this.primaryPeriodType = 'Initial';
        this.chartType = 'Initial';
        return;
      }

      if (type === 'Range') {
        this.primaryPeriodType = 'Range';
      } else {
        this.primaryPeriodType = type;
      }

      this.primaryPeriodValue = dayjs(dates[0]);
      this.chartType = this.primaryPeriodType;
    },
    setFiliterInputFromDates(dates, type = this.filiterPeriodType) {
      if (!dates || !dates[0]) return;

      if (type === 'Range') {
        this.filiterPeriodType = 'Range';
      } else if (type !== 'Initial') {
        this.filiterPeriodType = type;
      }

      this.filiterPeriodValue = dayjs(dates[0]);
    },
    setPrimaryPeriodType(type) {
      this.primaryPeriodType = type;

      if (type === 'Initial') {
        this.chartType = 'Initial';
        this.setInitialDate(['2010-01-01', '2110-01-01']);
        return;
      }

      this.chartType = type;

      if (type !== 'Range') {
        this.setPrimaryPeriodValue(this.primaryPeriodValue);
      }
    },
    setPrimaryPeriodValue(value) {
      const nextValue = value || dayjs();
      this.primaryPeriodValue = nextValue;
      this.chartType = this.primaryPeriodType;
      this.initialDateCompare = this.getPeriodDates(this.primaryPeriodType, nextValue);

      if (this.connectedCharts) {
        this.setFiliterDate(this.initialDateCompare, this.primaryPeriodType);
      }
    },
    setPrimaryRange(dates) {
      const nextRange = this.formatPickerRange(dates);
      if (!nextRange) return;

      this.primaryPeriodType = 'Range';
      this.chartType = 'Range';
      this.primaryPeriodValue = dayjs(nextRange[0]);
      this.initialDateCompare = nextRange;

      if (this.connectedCharts) {
        this.setFiliterDate(nextRange, 'Range');
      }
    },
    setFiliterPeriodType(type) {
      this.filiterPeriodType = type;

      if (type === 'Weekly') {
        this.setFiliterPeriodValue(dayjs());
        return;
      }

      if (type !== 'Range') {
        this.setFiliterPeriodValue(this.filiterPeriodValue);
      }
    },
    setFiliterPeriodTypeFromChart(type) {
      if (!type || type === 'Initial') return;

      this.filiterPeriodType = type;

      if (type === 'Weekly') {
        this.setFiliterPeriodValue(dayjs());
      }
    },
    setFiliterPeriodValue(value) {
      const nextValue = value || dayjs();
      this.filiterPeriodValue = nextValue;
      const dates = this.getPeriodDates(this.filiterPeriodType, nextValue);
      const startDate = dayjs(dates[0]);
      this.defaultFilterYear = startDate;
      this.startMonth = startDate.month() + 1;
      this.setFiliterDate(dates, this.filiterPeriodType);
    },
    setFiliterRange(dates) {
      const nextRange = this.formatPickerRange(dates);
      if (!nextRange) return;

      this.filiterPeriodType = 'Range';
      this.filiterPeriodValue = dayjs(nextRange[0]);
      this.defaultFilterYear = this.filiterPeriodValue;
      this.startMonth = this.filiterPeriodValue.month() + 1;
      this.setFiliterDate(nextRange, 'Range');
    },
    monthKey(month) {
      return String(month || '').toLowerCase();
    },
    toNumber(value) {
      if (Array.isArray(value)) {
        return this.getMonthlyStats({ all: value }).reduce((sum, item) => sum + item.count, 0);
      }

      if (value && typeof value === 'object') {
        if (value.trips_count || value.trips_count === 0) {
          return this.toNumber(value.trips_count);
        }
        if (value.trips_miles || value.trips_miles === 0) {
          return this.toNumber(value.trips_miles);
        }
        if (value.count || value.count === 0) {
          return this.toNumber(value.count);
        }
        return 0;
      }

      const bare = typeof value === 'string' ? value.replace(/,/g, '') : value;
      const parsed = Number(bare);

      return Number.isFinite(parsed) ? parsed : 0;
    },
    formatWholeNumber(value) {
      return this.toNumber(value).toLocaleString(undefined, {
        maximumFractionDigits: 0,
      });
    },
    formatPercent(value) {
      return `${this.toNumber(value).toFixed(1)}%`;
    },
    totalCommutersText(data) {
      if (!data || (!data.no_commuters && data.no_commuters !== 0)) {
        return '0 Commuters';
      }

      return `${this.formatWholeNumber(data.no_commuters)} Commuters`;
    },
    formatSignedNumber(value) {
      const number = this.toNumber(value);
      const formatted = this.formatWholeNumber(Math.abs(number));

      return `${number < 0 ? '' : ''}${formatted}`;
    },
    formatSignedPercent(value) {
      const number = this.toNumber(value);
      const formatted = `${Math.abs(number).toFixed(1)}%`;

      return `${number < 0 ? '' : ''}${formatted}`;
    },
    getMonthlyStats(modeData) {
      const months = sortAndNameMonthesAndCommuters(modeData?.all);

      return months.map((monthData) => {
        const month = Object.keys(monthData).find((key) => key !== 'commuters');

        return {
          month,
          count: this.toNumber(monthData?.[month]),
          commuters: this.toNumber(monthData?.commuters),
        };
      });
    },
    rowsToMap(rows) {
      return new Map(rows.map((row) => [row.key, row]));
    },
    buildCombinedModeRows(primaryData, selectedData) {
      const primaryMap = this.rowsToMap(this.buildModeRows(primaryData));
      const selectedMap = this.rowsToMap(this.buildModeRows(selectedData));
      const keys = new Set([...primaryMap.keys(), ...selectedMap.keys()]);

      return [...keys]
        .map((key) => {
          const primary = primaryMap.get(key);
          const selected = selectedMap.get(key);
          const primaryTrips = primary?.trips ?? 0;
          const selectedTrips = selected?.trips ?? 0;
          const primaryShare = primary?.share ?? 0;
          const selectedShare = selected?.share ?? 0;
          const tripsDiff = primaryTrips - selectedTrips;
          const shareDiff = primaryShare - selectedShare;

          return {
            key,
            label: primary?.label || selected?.label || key,
            primaryTrips,
            primaryTripsFormatted: this.formatWholeNumber(primaryTrips),
            primaryShare,
            primaryShareFormatted: this.formatPercent(primaryShare),
            primaryCombinedFormatted: this.formatCombinedValue(primaryTrips, primaryShare),
            selectedTrips,
            selectedTripsFormatted: this.formatWholeNumber(selectedTrips),
            selectedShare,
            selectedShareFormatted: this.formatPercent(selectedShare),
            selectedCombinedFormatted: this.formatCombinedValue(selectedTrips, selectedShare),
            tripsDiff,
            tripsDiffFormatted: this.formatSignedNumber(tripsDiff),
            shareDiff,
            shareDiffFormatted: this.formatSignedPercent(shareDiff),
            diffCombinedFormatted: `${this.formatSignedNumber(tripsDiff)} (${this.formatSignedPercent(shareDiff)})`,
          };
        })
        .sort((a, b) => a.label.localeCompare(b.label));
    },
    buildModeRows(data) {
      if (!data) return [];

      const rows = [];

      Object.entries(data).forEach(([key, val]) => {
        if (key === 'no_commuters' || key === 'custom_destructed') return;

        const monthlyStats = this.getMonthlyStats(val);
        const trips = monthlyStats.reduce((sum, item) => sum + item.count, 0);
        const commuters =
          monthlyStats.reduce((sum, item) => sum + item.commuters, 0) || this.toNumber(val?.count);

        rows.push({
          key,
          label: key,
          trips,
          commuters,
        });
      });

      if (data.custom_destructed) {
        Object.entries(data.custom_destructed).forEach(([key, val]) => {
          rows.push({
            key: `custom-${key}`,
            label: `${key} - CM*`,
            trips: this.toNumber(val),
            commuters: 0,
          });
        });
      }

      const totalTrips = rows.reduce((sum, item) => sum + item.trips, 0);

      return rows.map((row) => {
        const share = totalTrips ? (row.trips / totalTrips) * 100 : 0;

        return {
          ...row,
          share,
          tripsFormatted: this.formatWholeNumber(row.trips),
          shareFormatted: this.formatPercent(share),
          commutersFormatted: this.formatWholeNumber(row.commuters),
        };
      });
    },
    getInitialBehaviorValue(initialData, mode) {
      if (!initialData || (!initialData[mode] && initialData[mode] !== 0)) {
        return 0;
      }

      const modeData = initialData[mode];

      if (modeData?.all || modeData?.all === 0) {
        return this.toNumber(modeData.all);
      }

      return this.toNumber(modeData);
    },
    getBehaviorMonthTotals(data) {
      if (!data) return {};

      const totals = {};

      Object.entries(data).forEach(([key, val]) => {
        if (key === 'no_commuters' || key === 'initial' || key === 'custom_destructed') {
          return;
        }

        this.getMonthlyStats(val).forEach(({ month, count }) => {
          const keyName = this.monthKey(month);
          totals[keyName] = (totals[keyName] || 0) + count;
        });
      });

      return totals;
    },
    getInitialTotals(initialData) {
      if (!initialData) return 0;

      return Object.keys(initialData).reduce((sum, key) => {
        if (key === 'no_commuters' || key === 'custom_destructed') {
          return sum;
        }

        return sum + this.getInitialBehaviorValue(initialData, key);
      }, 0);
    },
    buildBehaviorRows(data) {
      if (!data) return [];

      const monthTotals = this.getBehaviorMonthTotals(data);
      const totalTripsAll = Object.values(monthTotals).reduce((sum, value) => sum + value, 0);
      const initialTotal = this.getInitialTotals(data.initial);

      return Object.entries(data).reduce((rows, [key, val]) => {
        if (key === 'no_commuters' || key === 'initial' || key === 'custom_destructed') {
          return rows;
        }

        const initialTrips = this.getInitialBehaviorValue(data.initial, key);
        const initialShare = initialTotal ? (initialTrips / initialTotal) * 100 : 0;

        const row = {
          key,
          label: key,
          initialTrips,
          initialShare,
          initialTripsFormatted: this.formatWholeNumber(initialTrips),
          initialShareFormatted: this.formatPercent(initialShare),
          initialCombinedFormatted: `${this.formatWholeNumber(initialTrips)} (${this.formatPercent(
            initialShare
          )})`,
        };

        let totalTrips = 0;

        this.getMonthlyStats(val).forEach(({ month, count }) => {
          const keyName = this.monthKey(month);
          const monthTotal = monthTotals[keyName] || 0;
          const percent = monthTotal ? (count / monthTotal) * 100 : 0;

          row[`${keyName}Trips`] = count;
          row[`${keyName}TripsFormatted`] = this.formatWholeNumber(count);
          row[`${keyName}Share`] = percent;
          row[`${keyName}ShareFormatted`] = this.formatPercent(percent);
          row[`${keyName}CombinedFormatted`] = `${this.formatWholeNumber(
            count
          )} (${this.formatPercent(percent)})`;

          totalTrips += count;
        });

        const totalShare = totalTripsAll ? (totalTrips / totalTripsAll) * 100 : 0;

        row.totalTrips = totalTrips;
        row.totalTripsFormatted = this.formatWholeNumber(totalTrips);
        row.totalShare = totalShare;
        row.totalShareFormatted = this.formatPercent(totalShare);
        row.totalCombinedFormatted = `${this.formatWholeNumber(
          totalTrips
        )} (${this.formatPercent(totalShare)})`;

        rows.push(row);

        return rows;
      }, []);
    },
    updateConnectedStore(val) {
      this.$store.state.stats.connectedCharts = val;
    },
    setReportDate(y) {
      this.reportYear = y;
      const currYear = dayjs(y).year();

      const reportDate = [`${currYear}-01-01`, `${currYear}-12-31`];
      this.reportDate = reportDate;
    },
    setFiliterDate(dates, type = this.filiterPeriodType) {
      this.filiterDate = dates;
      this.setFiliterInputFromDates(dates, type);
      if (this.connectedCharts) {
        this.initialDate = dates;
      }
      this.initialDateConnect = dates;
    },
    handleChange(value) {
      this.debouncedHandleChange(value);
    },
    setFiliterDateCompare(dates, type = this.primaryPeriodType) {
      this.initialDateCompare = dates;
      this.setPrimaryInputFromDates(dates, type);
      if (this.connectedCharts) {
        this.setFiliterDate(dates, type);
      }
    },
    setInitialDate(dates) {
      this.initialDate = dates;
    },
    updateDefaultType(type) {
      this.loading = true;
      setTimeout(() => {
        this.loading = false;
      }, 1000);
      if (type != 'Initial') {
        this.primaryPeriodType = type;
        this.primaryPeriodValue =
          type === 'Weekly'
            ? dayjs()
            : dayjs(this.initialDateCompare?.[0] || this.filiterDate?.[0] || dayjs());
        if (type !== 'Range') {
          this.initialDateCompare = this.getPeriodDates(type, this.primaryPeriodValue);
        }
        this.chartType = type;
        return;
      } else {
        // this.loading = true;
        this.initialDate = ['2010-01-01', '2110-01-01'];
        this.primaryPeriodType = 'Initial';
      }
      this.chartType = type;
    },
    openNotification() {
      notification.error({
        message: 'Something went wrong',
        description: 'Something went wrong while filtering, the page will be reloaded now !',
        duration: 6,
      });
    },
    forceRerender() {
      this.componentKey += 1;
    },
    setModeSummaryView(value) {
      this.modeSummaryView = value;
    },
    setBehaviorSummaryView(value) {
      this.behaviorSummaryView = value;
    },
  },
};
</script>
